import { TableColumn } from "./types";
import TableToolbar from "./TableToolbar";
import TablePagination from "./TablePagination";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

interface DataTableProps<T> {
  title: string;
  columns: TableColumn<T>[];
  data: T[];
  pagination: PaginationProps;

  showImport?: boolean;
  showFilter?: boolean;
  filterContent?: React.ReactNode;
  showAdd?: boolean;
  onAdd?: () => void;

  // BARU: search
  showSearch?: boolean;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
}

export default function DataTable<T extends Record<string, any>>({
  title,
  columns,
  data,
  pagination,
  showImport = true,
  showFilter = true,
  filterContent,
  showAdd = true,
  onAdd,
  showSearch = true,
  onSearch,
  searchPlaceholder,
}: DataTableProps<T>) {
  const { currentPage, totalPages, pageSize, onPageChange, onPageSizeChange,  } =
    pagination;

  return (
    <div className="overflow-hidden rounded-[32px] bg-white shadow-sm">
      <TableToolbar
        title={title}
        showImport={showImport}
        showFilter={showFilter}
        filterContent={filterContent}
        showAdd={showAdd}
        onAdd={onAdd}
        showSearch={showSearch}
        onSearch={onSearch}
        searchPlaceholder={searchPlaceholder}
      />

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-y bg-[#FAFAFA]">
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className="whitespace-nowrap px-6 py-4 text-left font-medium text-gray-500">
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-10 text-center text-gray-400">
                  No data available
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={row.id ?? index}
                  className="border-b transition hover:bg-gray-50">
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className="px-6 py-5 align-middle">
                      {column.render
                        ? column.render(row)
                        : String(row[column.key] ?? "-")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
